import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const ease = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const instruments = ["Equities", "Options", "Hedging", "Fixed Income", "Derivatives"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGrid = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16"
    >
      {/* Parallax financial grid + glow */}
      <motion.div style={{ y: yGrid }} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 fin-grid opacity-60" />
        <div className="absolute inset-0 fin-glow" />
      </motion.div>

      {/* Animated chart line */}
      <svg className="absolute right-0 bottom-0 w-full md:w-2/3 h-1/2 pointer-events-none opacity-70" viewBox="0 0 600 300" preserveAspectRatio="none">
        <motion.path
          d="M0,240 L80,200 L140,220 L210,150 L280,175 L350,90 L420,120 L490,55 L560,80 L600,30"
          fill="none"
          stroke="hsl(158 76% 46%)"
          strokeWidth="2.5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: "easeInOut", delay: 0.6 }}
        />
        <motion.path
          d="M0,240 L80,200 L140,220 L210,150 L280,175 L350,90 L420,120 L490,55 L560,80 L600,30 L600,300 L0,300 Z"
          fill="url(#g)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.12 }}
          transition={{ duration: 1.5, delay: 1.6 }}
        />
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(158 76% 46%)" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
      </svg>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="font-mono-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary mb-6"
          data-testid="hero-eyebrow"
        >
          / Educational Initiative by PBA Capital
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display font-black uppercase tracking-tighter leading-[0.85] text-[clamp(3rem,13vw,10rem)]"
        >
          <Line delay={0.25}>Design</Line>
          <Line delay={0.35}>
            <span className="text-primary">My</span>
          </Line>
          <Line delay={0.45}>Cashflow</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 max-w-5xl"
        >
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
            Engineering repeatable <span className="text-foreground font-semibold">cashflow models</span> across
            equities, hedging strategies and income instruments — built on discipline, not prediction.
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => {
              const el = document.getElementById("models");
              if (el) { if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 }); else el.scrollIntoView({ behavior: "smooth" }); }
            }}
            className="shrink-0 group inline-flex items-center gap-3 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wide px-7 py-4 hover:bg-[#1AE39C] transition-colors"
          >
            Explore The Models
            <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
          </button>
        </motion.div>

        {/* instrument ticker chips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-12 flex flex-wrap gap-2"
        >
          {instruments.map((i) => (
            <span key={i} className="font-mono-accent text-[10px] sm:text-xs uppercase tracking-[0.15em] border border-border px-3 py-1.5 text-muted-foreground">
              {i}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-0 right-0 z-10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-3">
          <ArrowDown size={16} className="text-primary animate-bounce" />
          <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            Scroll to explore
          </span>
        </div>
      </motion.div>
    </section>
  );
}
